// src/ui/Wheel.ts
//
// Phase 5 — static roulette wheel: 37 wedges drawn with Phaser.GameObjects.Graphics.
// Phase 6 — `spinTo(targetIndex)` tweens the wheel and orbits the ball.
// Phase 11 visual polish:
//   - Ball is a separate child of `this` (not `wheelGroup`), so it does NOT rotate
//     with the wheel. During `spinTo`, the ball orbits CCW around the rim and
//     ends positioned at the rim point that aligns with the result wedge.
//   - After `spinTo` settles, `showResult(slot)` highlights the result wedge with
//     a gold ring and shows "12 RED" text near the wheel.
//
// Phase 13 (UI-12, WHEEL-12):
//   - Default size reduced from 270 → 160px.
//   - `setSlots(slots)` API: accepts a custom run-wheel composition (Slot[]) and
//     re-draws wedges. Default without setSlots continues to use the static WHEEL.
//   - Void wedges render at #1a1a2e (deep navy) with the '∅' glyph (8px white).
//   - `showResult` narrows on slot.kind: void → '∅ VOID' #888888; number → 'N COLOR'.
//   - SPIN button moved to hub (RoundScene owns the circle; Wheel does not own it).

import Phaser from 'phaser';
import { WHEEL } from '../systems/wheel';
import type { Slot } from '../state/types';
import { addCrispText } from './textHelper';
import { ballSpritesheet } from '../content/balls';

/**
 * Returns the current rainbow hue in degrees [0, 360), driven by wall-clock
 * time at 60°/s (full spectrum every 6 seconds). Module-level so every caller
 * — regardless of which scene or instance — reads the same phase.
 */
export function globalRainbowHue(): number {
  return ((Date.now() / 1000) * 60) % 360;
}

export class Wheel extends Phaser.GameObjects.Container {
  private graphics: Phaser.GameObjects.Graphics;
  private sliceGraphics: Phaser.GameObjects.Graphics;
  private wheelGroup: Phaser.GameObjects.Container;
  private ballContainer: Phaser.GameObjects.Container;
  private resultRing: Phaser.GameObjects.Graphics;
  private resultText: Phaser.GameObjects.Text | null = null;
  private readonly radius: number;
  private readonly ballRadius = 5;
  // Phase 13: internal slot array; defaults to the static WHEEL constant.
  private slots: readonly Slot[] = WHEEL;
  // Stored slice data so tickSlices() can animate rainbow slots each frame.
  private _sliceScores: number[] = [];
  private _sliceTarget: number = 1;
  private _hasRainbow = false;
  // Number labels — children of wheelGroup so they rotate with the wheel.
  private numberLabels: Phaser.GameObjects.Text[] = [];

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    public readonly size: number = 160, // ← was 270 in v1; 160 per 13-UI-SPEC
  ) {
    super(scene, x, y);
    scene.add.existing(this);
    this.radius = size / 2;
    this.graphics = scene.add.graphics();
    this.sliceGraphics = scene.add.graphics();
    // sliceGraphics is a child of wheelGroup so it rotates with the wheel —
    // each slice stays aligned with its slot wedge during/after spins.
    this.wheelGroup = scene.add.container(0, 0, [this.graphics, this.sliceGraphics]);
    this.add(this.wheelGroup);
    this.drawWedges();

    // Result ring (drawn after wheel; hidden until showResult is called).
    this.resultRing = scene.add.graphics();
    this.add(this.resultRing);

    // Ball container — separate child of `this` so it does NOT rotate with
    // the wheel. Position updated during spinTo tween. Initial: top of wheel.
    this.ballContainer = scene.add.container(0, -this.radius + this.ballRadius + 1);
    this.refreshBallVisual('');
    this.add(this.ballContainer);
  }

  /**
   * Phase 13 (UI-12): accept a custom wheel composition from GameState.
   * Called once in RoundScene.create() after construction. Re-draws all
   * 37 wedges so void slots render with their correct #1a1a2e fill.
   */
  public setSlots(slots: readonly Slot[]): void {
    this.slots = slots;
    this.wheelGroup.removeAll(true); // destroys all children including old labels
    this.numberLabels = [];
    this.graphics = this.scene.add.graphics();
    this.sliceGraphics = this.scene.add.graphics();
    this.wheelGroup.add(this.graphics);
    this.wheelGroup.add(this.sliceGraphics);
    this.drawWedges();
  }

  /**
   * Store scores + remaining target and draw the initial frame.
   * `target` should be the REMAINING score needed (anteTarget − roundScore)
   * so a slot whose score fills the rest of the round reaches full length.
   * Animated rainbow slots are redrawn each frame via tickSlices().
   */
  public setScoreSlices(scores: number[], target: number): void {
    this._sliceScores = scores;
    this._sliceTarget = Math.max(target, 1);
    this._hasRainbow = scores.some((s) => s >= this._sliceTarget);
    this._drawSlices();
  }

  /** Call from RoundScene.update() to animate rainbow slots. No-op if no rainbow slots. */
  public tickSlices(): void {
    if (this._hasRainbow) this._drawSlices();
  }

  public clearScoreSlices(): void {
    this._sliceScores = [];
    this._hasRainbow = false;
    this.sliceGraphics.clear();
  }

  private _drawSlices(): void {
    this.sliceGraphics.clear();
    if (this._sliceScores.length === 0) return;
    const wedgeAngle = (2 * Math.PI) / 37;
    const hubR = 18;
    const innerR = hubR + 9.6; // minimum visible protrusion above hub
    const outerR = this.radius - 2;
    const target = this._sliceTarget;
    const hueOffset = globalRainbowHue();
    for (let i = 0; i < 37; i++) {
      const score = this._sliceScores[i] ?? 0;
      if (score <= 0) continue;
      const ratio = Math.min(score / target, 1);
      const sliceR = innerR + ratio * (outerR - innerR);
      const startAngle = i * wedgeAngle - Math.PI / 2;
      const endAngle = startAngle + wedgeAngle;
      let color: number;
      if (score >= target) {
        // Animated rainbow: each slot gets its own hue offset so they shimmer distinctly.
        color = hslToHex(((i / 37) * 360 + hueOffset) % 360, 100, 62);
      } else {
        color = 0xffd700;
      }
      this.sliceGraphics.fillStyle(color, 0.78);
      this.sliceGraphics.beginPath();
      this.sliceGraphics.moveTo(0, 0);
      this.sliceGraphics.arc(0, 0, sliceR, startAngle, endAngle, false);
      this.sliceGraphics.closePath();
      this.sliceGraphics.fillPath();
    }
  }

  private drawWedges(): void {
    const radius = this.radius;
    const wedgeAngle = (2 * Math.PI) / 37;

    this.graphics.clear();
    for (let i = 0; i < 37; i++) {
      const slot = this.slots[i]!;
      const startAngle = i * wedgeAngle - Math.PI / 2;
      const endAngle = startAngle + wedgeAngle;

      if (slot.kind === 'void') {
        // Void wedge: deep navy fill, thin grey stroke (per 13-UI-SPEC Color).
        this.graphics.fillStyle(0x1a1a2e, 1);
        this.graphics.beginPath();
        this.graphics.moveTo(0, 0);
        this.graphics.arc(0, 0, radius, startAngle, endAngle, false);
        this.graphics.closePath();
        this.graphics.fillPath();
        this.graphics.lineStyle(0.5, 0x666666, 1);
        this.graphics.strokePath();
      } else {
        // Number wedge: red/black/green per slot.color
        const color =
          slot.color === 'red' ? 0xb00020 : slot.color === 'black' ? 0x111111 : 0x2a8a3a;
        this.graphics.fillStyle(color, 1);
        this.graphics.beginPath();
        this.graphics.moveTo(0, 0);
        this.graphics.arc(0, 0, radius, startAngle, endAngle, false);
        this.graphics.closePath();
        this.graphics.fillPath();
        this.graphics.lineStyle(0.5, 0x000000, 1);
        this.graphics.strokePath();
      }
    }

    // Outer rim
    this.graphics.lineStyle(2, 0x6a4520, 1);
    this.graphics.strokeCircle(0, 0, radius);
    // Hub
    this.graphics.fillStyle(0x6a4520, 1);
    this.graphics.fillCircle(0, 0, 18);

    // Number labels — only rendered when the wheel is large enough (radius > 100)
    // so the ~22px arc per wedge can fit a 2-digit label at 8px.
    this.numberLabels = [];
    if (radius > 100) {
      const textR = radius - 16;
      for (let i = 0; i < 37; i++) {
        const slot = this.slots[i]!;
        if (slot.kind === 'void') continue;
        const midAngle = i * wedgeAngle - Math.PI / 2 + wedgeAngle / 2;
        const tx = Math.cos(midAngle) * textR;
        const ty = Math.sin(midAngle) * textR;
        const label = addCrispText(this.scene, tx, ty, String(slot.value), {
          fontSize: '8px',
          color: '#ffffff',
        })
          .setOrigin(0.5, 0.5)
          .setRotation(midAngle + Math.PI / 2);
        this.wheelGroup.add(label);
        this.numberLabels.push(label);
      }
    }
  }

  /** Swap the ball visual to match the active ball's defId. */
  public setBallDefId(defId: string): void {
    this.refreshBallVisual(defId);
  }

  private refreshBallVisual(defId: string): void {
    this.ballContainer.removeAll(true);
    const sz = this.ballRadius * 2 + 6;

    // Spritesheet check first, then per-ball key, then universal fallback
    const sheet = ballSpritesheet(defId);
    if (sheet && this.scene.textures.exists(sheet.key)) {
      this.ballContainer.add(
        this.scene.add.image(0, 0, sheet.key, sheet.frame).setDisplaySize(sz, sz),
      );
    } else {
      const texKey = `ball-${defId}`;
      const resolvedKey = this.scene.textures.exists(texKey)
        ? texKey
        : this.scene.textures.exists('ball-sprite')
          ? 'ball-sprite'
          : null;
      if (resolvedKey) {
        this.ballContainer.add(this.scene.add.image(0, 0, resolvedKey).setDisplaySize(sz, sz));
      } else {
        this.ballContainer.add(
          this.scene.add.circle(0, 0, this.ballRadius, 0xffffff).setStrokeStyle(1, 0x000000),
        );
      }
    }
  }

  /**
   * Animate the wheel and the ball. The wheel rotates 4 full turns + final wedge
   * angle clockwise (cubic ease-out, 2000ms) so the wedge at `targetIndex` ends
   * up at the top. Simultaneously the ball orbits CCW around the rim 4 full
   * turns and lands at the top — visually settling into the result wedge.
   * Resolves on tween complete.
   */
  public spinTo(targetIndex: number): Promise<void> {
    this.clearResult();
    return new Promise((resolve) => {
      const wedgeAngle = (Math.PI * 2) / 37;
      const fullRotations = 4;
      // Wedge `i` spans world angle [i*w - π/2, (i+1)*w - π/2] when the wheel
      // is unrotated. Its CENTER is at i*w - π/2 + w/2. To land that center
      // at world angle -π/2 (the ball position at the top), rotate by
      // R = -(i*w + w/2). Without the w/2 term, the ball lands on the boundary
      // between wedges i-1 and i.
      const finalAngle = -(targetIndex * wedgeAngle + wedgeAngle / 2);
      const totalRotation = fullRotations * Math.PI * 2 + finalAngle;
      const ballRimRadius = this.radius - this.ballRadius - 1;

      // Wheel tween
      this.scene.tweens.add({
        targets: this.wheelGroup,
        rotation: totalRotation,
        duration: 2000,
        ease: 'Cubic.easeOut',
      });

      // Ball orbit tween — CCW direction (negative orbit angle), ends at top
      // (orbitAngle 0). 4 full revolutions over the same 2s window.
      const orbitState = { angle: 0 };
      this.scene.tweens.add({
        targets: orbitState,
        angle: -fullRotations * Math.PI * 2,
        duration: 2000,
        ease: 'Cubic.easeOut',
        onUpdate: () => {
          // angle = 0 → ball at top; convert to (x, y) on rim.
          const a = orbitState.angle - Math.PI / 2;
          this.ballContainer.x = Math.cos(a) * ballRimRadius;
          this.ballContainer.y = Math.sin(a) * ballRimRadius;
        },
        onComplete: () => {
          // Snap to exact top in case of subpixel drift
          this.ballContainer.x = 0;
          this.ballContainer.y = -ballRimRadius;
          resolve();
        },
      });
    });
  }

  /**
   * Phase 13 (UI-12, WHEEL-12): showResult narrows on slot.kind.
   * - void slot → '∅ VOID' in #888888
   * - number slot → 'N COLOR' with red/black/green colorHex
   *
   * Highlights the result wedge with a gold ring and shows the label
   * below the wheel. Persists until `clearResult()` is called or the
   * next `spinTo()` starts.
   */
  public showResult(slot: Slot): void {
    this.clearResult();

    // Gold ring around the rim
    this.resultRing.clear();
    this.resultRing.lineStyle(3, 0xd4af37, 1); // Phase 17: Art Deco gold
    this.resultRing.strokeCircle(0, 0, this.radius + 2);

    let label: string;
    let colorHex: string;
    if (slot.kind === 'void') {
      label = '∅ VOID';
      colorHex = '#888888';
    } else {
      label = slot.value === 0 ? '0 GREEN' : `${slot.value} ${slot.color.toUpperCase()}`;
      colorHex = slot.color === 'red' ? '#ff5060' : slot.color === 'black' ? '#cccccc' : '#5dd07a';
    }

    // Place result above the wheel (board is directly below, so we go up).
    this.resultText = addCrispText(this.scene, 0, -(this.radius + 18), label, {
      color: colorHex,
      fontSize: '20px',
    }).setOrigin(0.5);
    this.add(this.resultText);
  }

  /** Remove the result ring and result text. Call before the next spin. */
  public clearResult(): void {
    this.resultRing.clear();
    if (this.resultText) {
      this.resultText.destroy();
      this.resultText = null;
    }
  }
}

function hslToHex(h: number, s: number, l: number): number {
  s /= 100;
  l /= 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const r = Math.round(f(0) * 255);
  const g = Math.round(f(8) * 255);
  const b = Math.round(f(4) * 255);
  return (r << 16) | (g << 8) | b;
}
